import { Validate, ValidateResult } from "react-hook-form";

import { FormValidationRules, RequiredRule, ValidateRule, ValidationFunction } from "../types/FormValidationRules";
import { mapFailuresToValidateResult } from "./mapFailuresToValidateResult";
import { splitFailuresToErrorsAndWarnings } from "./splitFailuresToErrorsAndWarnings";
import { ValidationFailure } from "./ValidationResult";

export const getValidate = <TInput, TRules extends FormValidationRules<TInput>>(
    rules: TRules | undefined,
    submitCount: number,
    setWarnings: (warnings: ValidationFailure[]) => void
): Validate | Record<string, Validate> | undefined => {
    if (!rules?.required && !rules?.validate) {
        return undefined;
    }

    let validate: Record<string, Validate> = {};

    if (rules?.required) {
        validate = { ...validate, isRequired: buildIsRequired(rules.required) };
    }

    if (rules?.validate) {
        validate = { ...validate, ...buildValidate(rules.validate, submitCount, setWarnings) };
    }

    return validate;
};

const buildValidate = <TInput>(
    validate: ValidateRule<TInput>,
    submitCount: number,
    setWarnings: (warnings: ValidationFailure[]) => void
): Record<string, Validate> => {
    if (typeof validate === "function") {
        return {
            validation: (input) => filterErrorsFromFailures(input, submitCount, validate, setWarnings)
        };
    }

    return Object.keys(validate).reduce<Record<string, Validate>>(
        (dict, key) => ({
            ...dict,
            [key]: (input) => filterErrorsFromFailures(input, submitCount, validate[key], setWarnings)
        }),
        {}
    );
};

const filterErrorsFromFailures = async <TInput>(
    input: TInput,
    submitCount: number,
    validate: ValidationFunction<TInput>,
    setWarnings: (warnings: ValidationFailure[]) => void
): Promise<ValidateResult> => {
    const response = await validate(input);

    if (response.isValid) {
        return true;
    }

    const { errorList, warningList } = splitFailuresToErrorsAndWarnings(response.failures);

    setWarnings(warningList);

    if (warningList.length && submitCount === 0) {
        return false;
    }

    if (!errorList.length) {
        return true;
    }

    return mapFailuresToValidateResult(errorList);
};

const buildIsRequired = (required: RequiredRule): Validate => (input: unknown) => {
    const message = required["message"] || (typeof required === "string" ? required : "Value is missing.");

    if (typeof input === "string" || Array.isArray(input)) {
        return input.length ? true : message;
    }

    return input ? true : message;
};
